<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 pb-4">
      <SimplePageHeader :title="pageTitle" class="px-4 pr-5 mb-3">
        <template v-slot:right-buttons>
          <div v-if="canManageInstance" class="d-flex align-center">
            <v-select
              v-model="selectedStatusId"
              :items="instanceStatusOptions"
              item-text="name"
              item-value="id"
              label="Change Status"
              outlined
              dense
              hide-details
              style="max-width: 180px;"
              class="mr-2"
            />
            <v-btn
              color="primary"
              outlined
              :disabled="!selectedStatusId"
              @click="handleUpdateStatus"
            >
              Update Status
            </v-btn>
          </div>
        </template>
      </SimplePageHeader>
      <v-row class="px-4">
        <v-col cols="12" sm="3">
          <div class="caption grey--text">Candidate</div>
          <div class="body-2">{{ candidateName || '—' }}</div>
        </v-col>
        <v-col cols="12" sm="3">
          <div class="caption grey--text">Client</div>
          <div class="body-2">{{ clientName || '—' }}</div>
        </v-col>
        <v-col cols="12" sm="3">
          <div class="caption grey--text">Target Start Date</div>
          <div class="body-2">{{ targetStartDate | formatDate }}</div>
        </v-col>
        <v-col cols="12" sm="3">
          <div class="caption grey--text">Status</div>
          <v-chip
            small
            label
            :color="instanceStatusAttrs(statusId).color"
            :dark="instanceStatusAttrs(statusId).dark"
          >{{ statusName || '—' }}</v-chip>
        </v-col>
      </v-row>
      <v-row class="px-4 mt-0">
        <v-col cols="12" sm="3">
          <div class="caption grey--text">Admin</div>
          <div class="body-2">{{ adminUserName || '—' }}</div>
        </v-col>
        <v-col cols="12" sm="3">
          <div class="caption grey--text">Deal ID</div>
          <div class="body-2">{{ dealId || '—' }}</div>
        </v-col>
        <v-col cols="12" sm="3">
          <div class="caption grey--text">Created</div>
          <div class="body-2">{{ createdAt | formatDate }}</div>
        </v-col>
      </v-row>
      <v-row class="px-4 pb-2 mt-1">
        <v-col cols="12" sm="3">
          <v-select
            v-model="taskStatusFilter"
            :items="taskStatusOptions"
            item-text="name"
            item-value="id"
            label="All Task Statuses"
            clearable
            outlined
            dense
            hide-details
          />
        </v-col>
      </v-row>
    </div>

    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="filteredTasks"
          class="elevation-2"
        >
          <template v-slot:item.title="{ item }">
            <span>{{ item.title }}</span>
            <v-chip v-if="isAutomatedTask(item)" x-small label color="purple" dark class="ml-1">
              <v-icon x-small left>flash_on</v-icon>{{ item.taskTypeId === workflowTaskType.AutomatedDocument ? 'Document' : 'Email' }}
            </v-chip>
            <div v-if="isAutomatedTask(item) && item.executedAt" class="caption grey--text">
              Executed {{ item.executedAt | formatDate }}
            </div>
            <div v-if="item.taskTypeId === workflowTaskType.AutomatedDocument && item.outputFileName" class="caption green--text">
              <v-icon x-small color="green">description</v-icon> {{ item.outputFileName }}
            </div>
            <div v-if="isAutomatedTask(item) && item.executionResult" class="caption red--text">
              {{ item.executionResult }}
            </div>
          </template>
          <template v-slot:item.statusName="{ item }">
            <v-chip
              x-small
              label
              :color="taskStatusAttrs(item.statusId).color"
              :dark="taskStatusAttrs(item.statusId).dark"
            >{{ item.statusName }}</v-chip>
            <v-chip
              v-if="item.reviewStatusId"
              x-small
              label
              class="ml-1"
              :color="reviewStatusAttrs(item.reviewStatusId).color"
              :dark="reviewStatusAttrs(item.reviewStatusId).dark"
            >{{ item.reviewStatusName }}</v-chip>
          </template>
          <template v-slot:item.dueDate="{ item }">
            <span :class="dueDateClass(item)">{{ item.dueDate | formatDate }}</span>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click="openEditDialog(item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn
              v-if="item.reviewStatusId === workflowEmailReviewStatus.PendingInternalReview && canManageInstance"
              class="mx-1 my-1"
              color="orange darken-1"
              outlined
              @click="$router.push(workflowEmailReviewQueueRoute.url)"
            >
              <v-icon color="orange darken-1">mark_email_read</v-icon>
            </v-btn>
            <v-btn
              v-if="canReExecute(item)"
              class="mx-1 my-1"
              color="orange darken-1"
              outlined
              :loading="reExecutingTaskId === item.id"
              @click="handleReExecute(item)"
            >
              <v-icon color="orange darken-1">flash_on</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>

    <WorkflowTaskEditDialog
      v-model="showEditDialog"
      :taskId="editingTaskId"
      @saved="refreshInstance"
    />

    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import WorkflowTaskEditDialog from './WorkflowTaskEditDialog'
import { ADMIN, SUPER_ADMIN, MANAGER } from '@/lib/constants/securityRoles'
import { userHasRoles, getCurrentUser } from '@/lib/authentication'
import workflowStatusMixin from './workflowStatusMixin'
import workflowEmailReviewStatusMixin from '@/components/workflowEmailSends/workflowEmailReviewStatusMixin'
import workflowTaskAutomationMixin from './workflowTaskAutomationMixin'
import { WORKFLOW_INSTANCE_STATUS_OPTIONS, WORKFLOW_TASK_STATUS_OPTIONS, workflowTaskStatus } from '@/lib/constants/workflowStatuses'
import { workflowTaskType, workflowEmailReviewStatus } from '@/lib/constants/workflowEmailStatuses'
import { workflowEmailReviewQueueRoute } from '@/lib/constants/routes'

export default {
  mixins: [workflowStatusMixin, workflowEmailReviewStatusMixin, workflowTaskAutomationMixin],
  components: {
    BottomSpacer,
    SimplePageHeader,
    WorkflowTaskEditDialog
  },
  data () {
    return {
      instanceStatusOptions: WORKFLOW_INSTANCE_STATUS_OPTIONS,
      taskStatusOptions: WORKFLOW_TASK_STATUS_OPTIONS,
      workflowTaskType,
      workflowEmailReviewStatus,
      workflowEmailReviewQueueRoute,
      showEditDialog: false,
      editingTaskId: null,
      selectedStatusId: null,
      taskStatusFilter: null,
      reExecutingTaskId: null,
      headers: [
        { text: 'Title', value: 'title', sortable: false },
        { text: 'Assigned To', value: 'assignedToUserName', sortable: false, width: '15%' },
        { text: 'Due Date', value: 'dueDate', align: 'center', sortable: false, width: '12%' },
        { text: 'Status', value: 'statusName', align: 'center', sortable: false, width: '18%' },
        { text: 'Blocked By', value: 'blockerTaskTitle', sortable: false, width: '15%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '8%' }
      ]
    }
  },
  computed: {
    ...mapFields('workflowInstance', [
      'statusId',
      'statusName',
      'adminUserName',
      'candidateName',
      'clientName',
      'targetStartDate',
      'dealId',
      'createdAt',
      'tasks'
    ]),
    pageTitle () {
      if (this.candidateName && this.clientName) return `${this.candidateName} — ${this.clientName}`
      return this.candidateName || this.clientName || 'Workflow Instance'
    },
    canManageInstance () {
      return userHasRoles({ user: getCurrentUser(), allowedRoles: [ADMIN, SUPER_ADMIN, MANAGER] })
    },
    isAdmin () {
      return userHasRoles({ user: getCurrentUser(), allowedRoles: [ADMIN, SUPER_ADMIN] })
    },
    filteredTasks () {
      if (!this.taskStatusFilter) return this.tasks
      return this.tasks.filter(t => t.statusId === this.taskStatusFilter)
    }
  },
  methods: {
    ...mapActions('workflowInstance', ['getWorkflowInstance', 'updateInstanceStatus', 'clearWorkflowInstance']),
    openEditDialog (task) {
      this.editingTaskId = task.id
      this.showEditDialog = true
    },
    canReExecute (task) {
      return this.isAdmin && this.isAutomatedTask(task) && task.statusId === workflowTaskStatus.OffTrack
    },
    async handleReExecute (task) {
      this.reExecutingTaskId = task.id
      try {
        await this.$store.dispatch('workflowTask/reExecuteTask', task.id)
        await this.refreshInstance()
      } catch (e) {
        // error already notified by store
      } finally {
        this.reExecutingTaskId = null
      }
    },
    async handleUpdateStatus () {
      if (!this.selectedStatusId) return
      const id = parseInt(this.$route.params.id)
      try {
        await this.updateInstanceStatus({ id, statusId: this.selectedStatusId })
        this.selectedStatusId = null
      } catch (e) {
        // error already notified by store
      }
    },
    async refreshInstance () {
      const id = parseInt(this.$route.params.id)
      await this.getWorkflowInstance({ id })
    },
    dueDateClass (task) {
      if (!task.dueDate) return ''
      const due = new Date(task.dueDate)
      const now = new Date()
      if (task.statusId === workflowTaskStatus.Done) return 'task-complete'
      if (due < now) return 'task-past-due'
      if (due.setHours(0, 0, 0, 0) === now.setHours(0, 0, 0, 0)) return 'task-today'
      return ''
    }
  },
  async created () {
    const id = parseInt(this.$route.params.id)
    await this.getWorkflowInstance({ id })
  },
  beforeDestroy () {
    this.clearWorkflowInstance()
  }
}
</script>

<style scoped>
.task-past-due {
  color: red;
  font-weight: bold;
}
.task-today {
  color: orange;
  font-weight: bold;
}
.task-complete {
  color: #409932;
}
</style>
